import type { OfferRow, OfferStatus, OfferCurrency } from "./offer-types"
import { getCurrencySymbol, DEFAULT_CURRENCY } from "@/lib/currencies"

export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

export function isTerminalStatus(status: OfferStatus): boolean {
  return (
    status === "accepted" ||
    status === "declined" ||
    status === "counter_offered" ||
    status === "expired" ||
    status === "withdrawn"
  )
}

export function isPendingStatus(status: OfferStatus): boolean {
  return status === "pending"
}

export function currencySymbol(currency: OfferCurrency | string | null | undefined): string {
  // Delegate to the canonical registry so every supported currency renders its
  // real symbol (¥, NT$, C$, kr, …) — a hand-rolled EUR/GBP/$ switch silently
  // showed "$" for JPY/TWD/CAD sellers.
  return getCurrencySymbol(currency || DEFAULT_CURRENCY)
}

export function normalizeOfferRow(raw: unknown): OfferRow | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>
  if (typeof r.id !== "string" || typeof r.listing_id !== "string") return null
  if (typeof r.buyer_id !== "string" || typeof r.seller_id !== "string") return null
  const amount = toFiniteNumber(r.amount)
  if (amount == null) return null
  const status = typeof r.status === "string" ? (r.status as OfferStatus) : "pending"
  const currency = typeof r.currency === "string" ? (r.currency as OfferCurrency) : "EUR"
  const eventVersion = toFiniteNumber(r.event_version) ?? 0
  const shippingAmount = toFiniteNumber(r.shipping_amount) ?? 0
  return {
    id: r.id,
    listing_id: r.listing_id,
    buyer_id: r.buyer_id,
    seller_id: r.seller_id,
    amount,
    status,
    message: typeof r.message === "string" ? r.message : null,
    shipping_method_id: typeof r.shipping_method_id === "string" ? r.shipping_method_id : null,
    shipping_amount: shippingAmount,
    currency,
    expires_at: typeof r.expires_at === "string" ? r.expires_at : new Date().toISOString(),
    parent_offer_id: typeof r.parent_offer_id === "string" ? r.parent_offer_id : null,
    client_offer_id: typeof r.client_offer_id === "string" ? r.client_offer_id : null,
    decline_reason: typeof r.decline_reason === "string" ? r.decline_reason : null,
    event_version: Number(eventVersion),
    accepted_at: typeof r.accepted_at === "string" ? r.accepted_at : null,
    declined_at: typeof r.declined_at === "string" ? r.declined_at : null,
    countered_at: typeof r.countered_at === "string" ? r.countered_at : null,
    withdrawn_at: typeof r.withdrawn_at === "string" ? r.withdrawn_at : null,
    expired_at: typeof r.expired_at === "string" ? r.expired_at : null,
    payment_deadline: typeof r.payment_deadline === "string" ? r.payment_deadline : null,
    payment_timeout_notified_at:
      typeof r.payment_timeout_notified_at === "string" ? r.payment_timeout_notified_at : null,
    settlement_order_id: typeof r.settlement_order_id === "string" ? r.settlement_order_id : null,
    settled_at: typeof r.settled_at === "string" ? r.settled_at : null,
    reminder_sent_at: typeof r.reminder_sent_at === "string" ? r.reminder_sent_at : null,
    created_at: typeof r.created_at === "string" ? r.created_at : new Date().toISOString(),
    updated_at: typeof r.updated_at === "string" ? r.updated_at : new Date().toISOString(),
  }
}

export function generateClientOfferId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return `co_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}
